import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { issueWidgetKey, listKeys, revokeKey } from '../api/portal';
import { copyText } from '../lib/clipboard';

export function KeysPage() {
  const qc = useQueryClient();
  const keys = useQuery({ queryKey: ['portal-keys'], queryFn: listKeys });
  const [label, setLabel] = useState('embed-production');
  const [env, setEnv] = useState<'live' | 'test'>('live');
  const [issued, setIssued] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const issue = useMutation({
    mutationFn: () => issueWidgetKey(label.trim() || 'embed', env),
    onSuccess: (res) => {
      setIssued(res.key);
      setCopied(false);
      qc.invalidateQueries({ queryKey: ['portal-keys'] });
    },
  });
  const revoke = useMutation({
    mutationFn: revokeKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-keys'] }),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    issue.mutate();
  };

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">Access</div>
        <h1 className="page-title">API keys</h1>
        <p className="page-sub">
          Long-lived widget key for your site. The full key is shown only once.
          Preview keys (30 min) are created on the Matches page.
        </p>
      </div>

      <form onSubmit={onSubmit} className="card p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Label</label>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} required />
        </div>
        <div>
          <label className="label">Environment</label>
          <select className="select" value={env} onChange={(e) => setEnv(e.target.value as 'live' | 'test')}>
            <option value="live">live (pk_widget_live_)</option>
            <option value="test">test (pk_widget_test_)</option>
          </select>
        </div>
        <button className="btn-primary" disabled={issue.isPending}>
          {issue.isPending ? 'Issuing…' : 'Issue widget key'}
        </button>
        {issue.isError && <span className="text-sm text-red-400">Could not issue key (max 8 active).</span>}
      </form>

      {issued && (
        <div className="card p-4 mb-4 border border-amber-600/40">
          <div className="text-xs uppercase text-amber-400 mb-2">Copy this key now - it will not be shown again</div>
          <code className="block text-xs break-all text-white">{issued}</code>
          <button
            type="button"
            className="btn-secondary text-xs mt-3"
            onClick={() => {
              void copyText(issued);
              setCopied(true);
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}

      <div className="card p-4">
        <table className="table">
          <thead>
            <tr><th>Label</th><th>Kind</th><th>Hint</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {(keys.data ?? []).map((k) => (
              <tr key={k.id}>
                <td>{k.label}</td>
                <td className="text-slate-400">{k.kind}</td>
                <td className="font-mono text-xs">{k.prefix}****{k.display_last4}</td>
                <td>{k.is_active ? 'active' : 'revoked'}</td>
                <td className="text-right">
                  {k.is_active && k.kind === 'widget' && k.label !== 'portal-preview' && (
                    <button
                      type="button"
                      className="btn-danger text-xs"
                      disabled={revoke.isPending}
                      onClick={() => {
                        if (confirm(`Revoke ${k.label}? Embeds using this key will stop working.`)) {
                          revoke.mutate(k.id);
                        }
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
